<template>
    <el-dialog
        :model-value="modelValue"
        @update:modelValue="$emit('update:modelValue', $event)"
        class="submit-modal"
        width="500px"
        :show-close="false"
        center
        :title="
            $trans('web_profile.upload_photo_for') +
            ' ' +
            (WebProfileStore.profile.name || '')
        "
    >
        <template #header="{ close }">
            <div class="custom-header">
                <span @click="close" class="close-text">
                    {{ $trans("web_profile.cancel") }}
                    <el-icon class="custom-close-icon"><Close /></el-icon>
                </span>
            </div>
        </template>

        <div class="modal-body-content">
            <div class="photo-info-text">
                {{ $trans("web_profile.publish_photo_desc") }}
            </div>
            <el-upload
                class="upload-section"
                drag
                accept="image/*"
                :on-change="onPhotoChange"
                :auto-upload="false"
                :limit="1"
                :on-exceed="onExceed"
                ref="uploader"
                action="#"
            >
                <el-icon class="el-icon--upload"><upload-filled /></el-icon>
                <div class="el-upload__text">
                    {{ $trans("profile_dossier.click_here_or_drag_and_drop") }}
                </div>
                <template #tip>
                    <div class="el-upload__tip">
                        {{
                            $trans("profile_dossier.only_jpg_png_allowed") ||
                            "Only image files allowed"
                        }}
                    </div>
                </template>
            </el-upload>
        </div>

        <template #footer>
            <div class="dialog-footer">
                <el-button
                    class="grey-button"
                    @click="$emit('update:modelValue', false)"
                >
                    {{ $trans("web_profile.cancel") }}
                </el-button>
                <el-button
                    class="blue-button"
                    type="primary"
                    @click="handleSave"
                >
                    {{ $trans("messages.parse_view.save") || "Save" }}
                </el-button>
            </div>
        </template>
    </el-dialog>
</template>

<script>
import { mapStores } from "pinia";
import { useWebProfileStore } from "@/stores/WebProfileStore";
import { Close, UploadFilled } from "@element-plus/icons-vue";
import axios from "axios";

export default {
    name: "UploadPhotoModal",
    components: {
        Close,
        UploadFilled,
    },
    props: {
        modelValue: {
            type: Boolean,
            required: true,
        },
        profileId: {
            type: [Number, String],
            required: true,
        },
    },
    emits: ["update:modelValue", "photo-updated"],
    data() {
        return {
            selectedFile: null,
        };
    },
    computed: {
        ...mapStores(useWebProfileStore),
    },
    watch: {
        modelValue(val) {
            if (val) {
                this.selectedFile = null;
                if (this.$refs.uploader) {
                    this.$refs.uploader.clearFiles();
                }
            }
        },
    },
    methods: {
        onPhotoChange(file, fileList) {
            const rawFile = file.raw;

            const isImage = rawFile.type && rawFile.type.startsWith("image/");
            const MAX_SIZE = 14 * 1024 * 1024;

            if (!isImage) {
                this.$message.error(
                    this.$trans("profile_dossier.only_images") ||
                        "Please upload images only",
                );
                this.$refs.uploader.handleRemove(file);
                this.selectedFile = null;
                return;
            }

            if (rawFile.size > MAX_SIZE) {
                this.$message.error(
                    this.$trans("profile_dossier.image_too_large") ||
                        "Image is too large. Max size is 14 MB.",
                );
                this.$refs.uploader.handleRemove(file);
                this.selectedFile = null;
                return;
            }

            this.selectedFile = rawFile;
        },

        onExceed(files) {
            this.$refs.uploader.clearFiles();
            const file = files[0];
            this.$refs.uploader.handleStart(file);
        },

        async handleSave() {
            if (!this.selectedFile) {
                this.$message.warning(
                    this.$trans("profile_dossier.select_photo_first") ||
                        "Please select a photo first",
                );
                return;
            }

            const formData = new FormData();
            formData.append("photo", this.selectedFile);

            const token = document.head.querySelector(
                'meta[name="csrf-token"]',
            )?.content;

            try {
                this.$functions.showLoader();
                const response = await axios.post(
                    `/candidates/${this.profileId}/web-profile/process-image`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                            "X-CSRF-TOKEN": token,
                        },
                    },
                );

                if (response.data?.base64) {
                    this.$emit("photo-updated", response.data.base64);
                    this.$message.success(
                        this.$trans("profile_dossier.photo_saved_successfully"),
                    );
                    this.$emit("update:modelValue", false);
                } else {
                    this.$message.error("Error while processing image.");
                }
            } catch (error) {
                console.error(error);
                this.$message.error("Error while sending.");
            } finally {
                this.$functions.hideLoader();
            }
        },
    },
};
</script>

<style scoped>
.submit-modal :deep(.el-dialog__header) {
    padding: 15px 20px 10px;
    margin-right: 0;
    border-bottom: none;
    text-align: left;
}
.submit-modal :deep(.el-dialog__title) {
    font-size: 1.5rem;
}
.submit-modal :deep(.el-dialog__body) {
    padding: 10px 30px 20px;
    color: #3c3c3c;
    text-align: center;
}
.submit-modal :deep(.el-dialog__footer) {
    border-top: none;
    padding: 10px 20px 20px;
}
.custom-header {
    display: flex;
    justify-content: flex-end;
    position: absolute;
    top: 5px;
    right: 21px;
    font-size: 14px;
    color: #909399;
    font-weight: 500;
}
.close-text {
    cursor: pointer;
    display: flex;
    align-items: center;
}
.close-text:hover {
    color: #41abc4;
}
.dialog-footer {
    display: flex;
    gap: 15px;
    width: 100%;
}
.dialog-footer .el-button {
    flex-grow: 1;
    width: 100%;
    font-size: 16px;
    padding: 18px 10px;
    border-radius: 0;
    font-weight: 700;
}
.grey-button {
    background-color: #f2f2f2;
    border-color: #f2f2f2;
    color: #606266;
}
.blue-button {
    background-color: #41abc4;
    border-color: #41abc4;
    color: white;
}
.modal-body-content {
    display: flex;
    justify-content: center;
    width: 100%;
    flex-direction: column;
    align-items: center;
}
.photo-info-text {
    width: 100%;
    text-align: left;
    margin-bottom: 15px;
    font-size: 16px;
    font-weight: 500;
}
.upload-section {
    width: 100%;
}
.custom-close-icon {
    font-size: 20px;
    margin-left: 5px;
}
</style>
